import {Trans} from '@lingui/macro';
import React, {FC} from 'react';
import styled from 'styled-components';

import {Handler, HandlerOf} from '../../../../../../../libs/shared/helpers/src/react/reactHelpers';
import {Toggle} from '../../../../../../../libs/shared/ui/src/components/booleanInputs/toggle';
import {Button} from '../../../../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {VisualSizesEnum} from '../../../../../../../libs/shared/ui/src/styles/commonStyles';
import {fontSizes, fontWeights} from '../../../../../../../libs/shared/ui/src/styles/fontStyles';
import {useLinkNavigation} from '../../../../../../components/common/navigation/useLinkNavigation';
import {useAppDispatch, useAppSelector} from '../../../../../../helpers/reduxHelpers';
import {
  IntegrationSettingsTypesEnum,
  SlackIntegrationSettingsModel,
} from '../../../../../../models/api/integrationModel';
import {
  signInToIntegrationAccountAction,
  signOutOfIntegrationAsyncAction,
} from '../../../../../../store/data/integrations/integrationsActions';
import {getIsUserGlobalAdmin} from '../../../../../../store/data/teammates/teammatesSelectors';

/*
 * Constants.
 */

const seeHowItWorksLink = 'http://help.frontapp.com/t/k96lza/how-to-enable-and-use-the-slack-integration';

/*
 * Props.
 */

interface SettingsAppsConfigureSlackProps {
  configurationSettings?: SlackIntegrationSettingsModel;
  setConfigurationSettings: HandlerOf<SlackIntegrationSettingsModel | undefined>;
}

/*
 * Style.
 */

const StyledSeeHowItWorksWrapperDiv = styled.div`
  display: flex;
  margin-left: -14px;
  margin-bottom: 16px;
`;

const StyledToggleWrapperDiv = styled.div`
  display: flex;
  flex-flow: row;
  gap: 16px;
  margin-bottom: 16px;
`;

const StyledToggleTextWrapperDiv = styled.div`
  display: flex;
  flex-flow: column;
  gap: 4px;
`;

const StyledToggleTitleDiv = styled.div`
  font-weight: ${fontWeights.semibold};
  font-size: ${fontSizes.large};
`;

const StyledToggleDescriptionDiv = styled.div`
  font-size: ${fontSizes.verySmall};
  color: ${(p) => p.theme.greys.shade60};
`;

const StyledToggleDiv = styled.div`
  flex: 0 0 30px;
`;

const StyledButtonWrapperDiv = styled.div`
  display: flex;
  margin-top: 8px;
`;

/*
 * Component.
 */

export const SettingsAppsConfigureSlack: FC<SettingsAppsConfigureSlackProps> = (props) => {
  const {configurationSettings, setConfigurationSettings} = props;
  const {navigateToLink} = useLinkNavigation();
  const dispatch = useAppDispatch();
  const isGlobalAdmin = useAppSelector(getIsUserGlobalAdmin);

  const onSignInClick = () => {
    dispatch(signInToIntegrationAccountAction(IntegrationSettingsTypesEnum.SLACK, 'Slack'));
  };

  const onSignOutClick = () => {
    dispatch(signOutOfIntegrationAsyncAction(IntegrationSettingsTypesEnum.SLACK));
  };

  const onInteractiveToggle = () => {
    if (!isGlobalAdmin) {
      return;
    }
    setConfigurationSettings({
      ...configurationSettings,
      interactive: !configurationSettings?.interactive,
    });
  };

  return (
    <>
      <Trans id="settings-apps-configure-app-dialog-slack-description">
        Adds a &quot;Send to Slack&quot; item to the message menu and new actions to the rules.
      </Trans>
      <StyledSeeHowItWorksWrapperDiv>
        <Button variant={ButtonVariantsEnum.LINK} onClick={() => navigateToLink(seeHowItWorksLink)}>
          <Trans id="settings-apps-configure-app-dialog-slack-see-how-it-works">See how it works</Trans>
        </Button>
      </StyledSeeHowItWorksWrapperDiv>
      {renderToggle(configurationSettings, isGlobalAdmin, onInteractiveToggle)}
      {maybeRenderConnectedDetails(configurationSettings, isGlobalAdmin, onSignOutClick)}
      {maybeRenderNotConnectedDetails(configurationSettings, isGlobalAdmin, onSignInClick)}
    </>
  );
};

/*
 * Helpers.
 */

function renderToggle(
  configurationSettings: SlackIntegrationSettingsModel | undefined,
  isGlobalAdmin: boolean,
  onInteractiveToggle: Handler,
) {
  return (
    <StyledToggleWrapperDiv>
      <StyledToggleTextWrapperDiv onClick={onInteractiveToggle}>
        <StyledToggleTitleDiv>
          <Trans id="settings-apps-configure-app-dialog-slack-interactive-toggle-title">
            Enable interactive message menus
          </Trans>
        </StyledToggleTitleDiv>
        <StyledToggleDescriptionDiv>
          <Trans id="settings-apps-configure-app-dialog-slack-interactive-toggle-description">
            Allows your team to assign, move or archive Front conversations from Slack.
          </Trans>
        </StyledToggleDescriptionDiv>
      </StyledToggleTextWrapperDiv>
      <StyledToggleDiv>
        <Toggle
          isChecked={Boolean(configurationSettings?.interactive)}
          onChange={onInteractiveToggle}
          size={VisualSizesEnum.SMALL}
          isDisabled={!isGlobalAdmin}
        />
      </StyledToggleDiv>
    </StyledToggleWrapperDiv>
  );
}

function maybeRenderConnectedDetails(
  configurationSettings: SlackIntegrationSettingsModel | undefined,
  isGlobalAdmin: boolean,
  onSignOutClick: Handler,
) {
  if (!configurationSettings || !configurationSettings.readonly?.team) {
    return null;
  }
  return (
    <>
      <Trans id="settings-apps-configure-app-dialog-slack-connected-description">
        Your company is connected as <strong>{configurationSettings.readonly.team.name}</strong>
      </Trans>
      <StyledButtonWrapperDiv>
        <Button
          variant={ButtonVariantsEnum.SECONDARY_DANGER}
          onClick={onSignOutClick}
          isDisabled={!isGlobalAdmin}
        >
          <Trans id="settings-apps-configure-app-dialog-slack-sign-out-button">Sign out of Slack</Trans>
        </Button>
      </StyledButtonWrapperDiv>
    </>
  );
}

function maybeRenderNotConnectedDetails(
  configurationSettings: SlackIntegrationSettingsModel | undefined,
  isGlobalAdmin: boolean,
  onSignInClick: Handler,
) {
  if (configurationSettings?.readonly?.team) {
    return null;
  }
  return (
    <>
      <Trans id="settings-apps-configure-app-dialog-slack-not-connected-description">
        Connect to enable &quot;Send to Slack&quot; in the rules.
      </Trans>
      <StyledButtonWrapperDiv>
        <Button variant={ButtonVariantsEnum.PRIMARY} onClick={onSignInClick} isDisabled={!isGlobalAdmin}>
          <Trans id="settings-apps-configure-app-dialog-slack-sign-in-button">Sign in to Slack</Trans>
        </Button>
      </StyledButtonWrapperDiv>
    </>
  );
}
